import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Form,
  Input,
  Button,
  Space,
  Typography,
  Switch,
  Select,
  Tag,
  message,
  Spin,
  Alert,
} from 'antd';
import { SaveOutlined, CloseOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import {
  addTestPrompt,
  updateTestPrompt,
  getTestPromptById,
  type AgentTestPrompt,
} from '@/api/agentTestPrompts';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// TestPromptEditorDrawer — DB-first authoring for one ai.AiAgentTestPrompt row.
//
// Layout: form on the left, live preview on the right. Mirrors the smart-
// editor pattern (skill / scenario / instruction) for consistency, but
// without an AI co-pilot — test prompts are short and authoring fatigue
// is low; we can add AI drafting later if needed.
//
// Title uniqueness is enforced at the DB layer (UX_AiAgentTestPrompt_Title_AgentId
// unique index). The client also pre-checks against the open list so the
// user gets immediate feedback rather than a save-time error.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  agentId: number;
  /** Null = create new; number = edit existing. */
  promptId: number | null;
  /** Lowercased titles already in use by this agent (excluding the one being edited). */
  existingTitles: string[];
  onClose: () => void;
  onSaved?: (prompt: AgentTestPrompt) => void | Promise<void>;
}

interface FormValues {
  title: string;
  code?: string;
  category?: string;
  subcategory?: string;
  role?: string;
  prompt: string;
  whatThisTests?: string;
  whatToLookFor?: string;
  tags?: string;
  safetyCritical: boolean;
  peerConsultationExpected: boolean;
  isActive: boolean;
}

const blank = (): FormValues => ({
  title: '',
  code: '',
  category: '',
  subcategory: '',
  role: '',
  prompt: '',
  whatThisTests: '',
  whatToLookFor: '',
  tags: '',
  safetyCritical: false,
  peerConsultationExpected: false,
  isActive: true,
});

export const TestPromptEditorDrawer = observer(function TestPromptEditorDrawer({
  open,
  agentId,
  promptId,
  existingTitles,
  onClose,
  onSaved,
}: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [form] = Form.useForm<FormValues>();
  const [values, setValues] = useState<FormValues>(blank());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<AgentTestPrompt | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (promptId == null) {
      const fresh = blank();
      form.setFieldsValue(fresh);
      setValues(fresh);
      setEditingPrompt(null);
      return;
    }
    setLoading(true);
    getTestPromptById(promptId)
      .then((p) => {
        const next: FormValues = {
          title: p.title ?? '',
          code: p.code ?? '',
          category: p.category ?? '',
          subcategory: p.subcategory ?? '',
          role: p.role ?? '',
          prompt: p.prompt,
          whatThisTests: p.whatThisTests ?? '',
          whatToLookFor: p.whatToLookFor ?? '',
          tags: p.tags ?? '',
          safetyCritical: p.safetyCritical,
          peerConsultationExpected: p.peerConsultationExpected,
          isActive: p.isActive,
        };
        form.setFieldsValue(next);
        setValues(next);
        setEditingPrompt(p);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, promptId]);

  // Lint warnings — same UX pattern as the other smart editors.
  const lints = useMemo(() => computeLints(values, existingTitles, promptId != null), [
    values,
    existingTitles,
    promptId,
  ]);

  const handleSave = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      setError(null);

      const body: Partial<AgentTestPrompt> = {
        ...editingPrompt,
        aiAgentId: agentId,
        title: v.title.trim(),
        code: v.code?.trim() || null,
        category: v.category?.trim() || null,
        subcategory: v.subcategory?.trim() || null,
        role: v.role?.trim() || null,
        prompt: v.prompt,
        whatThisTests: v.whatThisTests?.trim() || null,
        whatToLookFor: v.whatToLookFor?.trim() || null,
        tags: v.tags?.trim() || null,
        safetyCritical: v.safetyCritical,
        peerConsultationExpected: v.peerConsultationExpected,
        isActive: v.isActive,
      };

      const saved =
        promptId == null
          ? await addTestPrompt(body)
          : await updateTestPrompt({ ...body, id: promptId } as AgentTestPrompt);

      void message.success(promptId == null ? 'Test prompt created.' : 'Saved.');
      if (onSaved) await onSaved(saved);
    } catch (err) {
      const msg = (err as { message?: string })?.message;
      if (msg) setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <ThunderboltOutlined style={{ color: colors.violet[500] }} />
          <span>{promptId == null ? 'New test prompt' : 'Edit test prompt'}</span>
          {editingPrompt?.code && (
            <Tag color="violet" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {editingPrompt.code}
            </Tag>
          )}
        </Space>
      }
      width="80%"
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button icon={<CloseOutlined />} onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            Save
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 0.8fr)',
            gap: 16,
            height: '100%',
          }}
        >
          {/* Left: form */}
          <div style={{ overflowY: 'auto', paddingRight: 8 }}>
            {error && (
              <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} style={{ marginBottom: 12 }} />
            )}
            <Form
              form={form}
              layout="vertical"
              initialValues={values}
              onValuesChange={(_, all) => setValues(all)}
            >
              <Space.Compact block style={{ marginBottom: 12 }}>
                <Form.Item
                  label="Title"
                  name="title"
                  style={{ flex: 3, marginBottom: 0 }}
                  rules={[{ required: true, message: 'Title is required' }]}
                >
                  <Input placeholder="Short heading shown on the card" />
                </Form.Item>
                <Form.Item
                  label="Code"
                  name="code"
                  style={{ flex: 1, marginBottom: 0, marginLeft: 12 }}
                  tooltip="Stable short handle used in bug reports — e.g. T1, GLU-03."
                >
                  <Input placeholder="T1" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
                </Form.Item>
              </Space.Compact>

              <Space.Compact block style={{ marginBottom: 12 }}>
                <Form.Item label="Category" name="category" style={{ flex: 1, marginBottom: 0 }}>
                  <Input placeholder="e.g. CGM Data" />
                </Form.Item>
                <Form.Item label="Subcategory" name="subcategory" style={{ flex: 1, marginBottom: 0, marginLeft: 12 }}>
                  <Input placeholder="optional" />
                </Form.Item>
                <Form.Item label="Role" name="role" style={{ flex: 1, marginBottom: 0, marginLeft: 12 }}>
                  <Select
                    allowClear
                    placeholder="Patient / Nurse / …"
                    options={[
                      { value: 'Patient', label: 'Patient' },
                      { value: 'Nurse', label: 'Nurse' },
                      { value: 'Physician', label: 'Physician' },
                      { value: 'Care Coordinator', label: 'Care Coordinator' },
                      { value: 'Pharmacist', label: 'Pharmacist' },
                      { value: 'Admin', label: 'Admin' },
                    ]}
                  />
                </Form.Item>
              </Space.Compact>

              <Form.Item
                label="Prompt"
                name="prompt"
                rules={[{ required: true, message: 'Prompt body is required' }]}
                tooltip="The exact text the tester copies into the chat."
              >
                <TextArea
                  rows={6}
                  style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}
                  placeholder="Show me an overview of Eric Smith's glucose trends for January 2026."
                />
              </Form.Item>

              <Form.Item label="What this tests" name="whatThisTests">
                <TextArea rows={2} placeholder="One-line description of the behavior under test." />
              </Form.Item>

              <Form.Item
                label="What to look for"
                name="whatToLookFor"
                tooltip="Markdown / multi-line. Renders as a checklist on the card. Use - bullets."
              >
                <TextArea
                  rows={5}
                  placeholder={'- Response includes time in range %\n- References actual glucose values in mg/dL\n- Exercises BloodGlucosePlugin'}
                />
              </Form.Item>

              <Form.Item label="Tags" name="tags" tooltip="Comma-separated. Free-form filter dimension.">
                <Input placeholder="regression, edge-case, smoke" />
              </Form.Item>

              <Space.Compact block>
                <Form.Item label="Safety-critical" name="safetyCritical" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="Peer consultation expected"
                  name="peerConsultationExpected"
                  valuePropName="checked"
                  style={{ marginBottom: 0, marginLeft: 24 }}
                >
                  <Switch />
                </Form.Item>
                <Form.Item label="Active" name="isActive" valuePropName="checked" style={{ marginBottom: 0, marginLeft: 24 }}>
                  <Switch />
                </Form.Item>
              </Space.Compact>
            </Form>
          </div>

          {/* Right: live preview */}
          <div
            style={{
              overflowY: 'auto',
              background: isDark ? colors.slate[900] : colors.slate[50],
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              borderRadius: 8,
              padding: 12,
            }}
          >
            <Text strong style={{ fontSize: 12 }}>
              CARD PREVIEW
            </Text>
            <div
              style={{
                marginTop: 8,
                background: isDark ? colors.slate[800] : '#FFFFFF',
                border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                borderLeft: `4px solid ${
                  values.safetyCritical
                    ? colors.red[600]
                    : values.peerConsultationExpected
                    ? colors.violet[500]
                    : colors.slate[300]
                }`,
                borderRadius: 10,
                padding: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                {values.code && (
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '2px 7px',
                      background: colors.violet[600],
                      color: '#FFFFFF',
                      borderRadius: 4,
                    }}
                  >
                    {values.code}
                  </span>
                )}
                <Text strong style={{ flex: 1 }}>{values.title || '(untitled)'}</Text>
                {values.role && <Tag color="blue">{values.role}</Tag>}
                {values.safetyCritical && <Tag color="red">Safety</Tag>}
                {values.peerConsultationExpected && <Tag color="purple">Peer consult</Tag>}
              </div>
              <pre
                style={{
                  margin: 0,
                  padding: '10px 12px',
                  background: isDark ? colors.slate[900] : colors.slate[50],
                  border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                  borderRadius: 8,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: isDark ? colors.slate[100] : colors.slate[800],
                }}
              >
                {values.prompt || '(empty prompt)'}
              </pre>
              {values.whatThisTests && (
                <Paragraph style={{ marginTop: 8, marginBottom: 0, fontSize: 12.5, fontStyle: 'italic' }}>
                  <strong style={{ fontStyle: 'normal' }}>Tests: </strong>
                  {values.whatThisTests}
                </Paragraph>
              )}
              {values.whatToLookFor && (
                <pre
                  style={{
                    marginTop: 8,
                    background: isDark ? colors.slate[900] + '88' : colors.slate[50],
                    border: `1px dashed ${isDark ? colors.slate[700] : colors.slate[200]}`,
                    borderRadius: 6,
                    padding: '8px 10px',
                    fontSize: 12.5,
                    fontFamily: 'inherit',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {values.whatToLookFor}
                </pre>
              )}
            </div>

            {/* Lint */}
            {lints.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Text strong style={{ fontSize: 12 }}>
                  LINT
                </Text>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                  {lints.map((l, i) => (
                    <li key={i} style={{ fontSize: 12.5, color: isDark ? colors.amber[500] : colors.amber[700] }}>
                      {l}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
});

function computeLints(v: FormValues, existingTitles: string[], _isEdit: boolean): string[] {
  const out: string[] = [];
  if (!v.title?.trim()) out.push('Title is empty.');
  else if (existingTitles.includes(v.title.trim().toLowerCase())) {
    out.push('Title already in use by another prompt for this agent — save will fail at the DB.');
  }
  if (!v.prompt?.trim()) out.push('Prompt body is empty.');
  if (v.prompt && v.prompt.length < 10) out.push('Prompt is very short — testers won\'t have much to send.');
  if (!v.whatThisTests?.trim()) out.push('"What this tests" is empty — list view will be vague.');
  if (!v.whatToLookFor?.trim()) out.push('"What to look for" is empty — testers can\'t verify the response.');
  return out;
}
